import { auditTargetTypeSchema } from '@planuze/admin-contracts';
import {
  Button,
  InlineAlert,
  Page,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
} from '@planuze/ui';
import { getRouteApi } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { auditTargetTypeLabel } from '../components/audit/audit-action-catalog.js';
import {
  AuditFilters,
  type AuditFiltersValue,
  defaultAuditFilters,
  periodToRange,
} from '../components/audit/audit-filters.js';
import { AuditTable } from '../components/audit/audit-table.js';
import { useAuditContent } from '../content/sections/audit.js';
import { useAuditLog } from '../hooks/use-audit.js';
import { useDebounce } from '../hooks/use-debounce.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import type { AuditQuery } from '../lib/audit-api.js';

/* Search params do deep-link "Ver auditoria completa" do detalhe de user/empresa (ADR-0713):
   escopam a trilha por um alvo (`targetType` + `targetId`). `safeParse` + fallback `{}` garante
   que uma URL malformada caia na trilha completa em vez de derrubar a tela. */
const auditSearchSchema = z.object({
  targetType: auditTargetTypeSchema.optional(),
  targetId: z.string().min(1).optional(),
});
type AuditSearch = z.infer<typeof auditSearchSchema>;

export function validateAuditSearch(search: Record<string, unknown>): AuditSearch {
  const parsed = auditSearchSchema.safeParse(search);
  return parsed.success ? parsed.data : {};
}

const routeApi = getRouteApi('/authenticated/audit');

/* Tela da trilha de auditoria (ADR-0708). Molde de `companies-list-page`: header + toolbar de
   filtros + Table paginada por cursor + "Carregar mais". Todos os filtros são server-side na
   query (busca livre `q`, ID do operador, tipo/ação por igualdade, janela do período). O deep-link
   do detalhe (`targetType`/`targetId`) pré-preenche o escopo por alvo (ADR-0713). */
export function AuditPage() {
  const copy = useAuditContent();
  const session = useStaffSession();
  const search = routeApi.useSearch();
  /* self-scoped (support/billing_ops) não filtram por operador — o servidor força o actorId. */
  const showActorFilter = session?.role === 'super_admin' || session?.role === 'readonly';
  /* Seed único a partir da URL — a partir daí o estado é local (mesmo padrão das list pages). */
  const [filters, setFilters] = useState<AuditFiltersValue>(() => ({
    ...defaultAuditFilters(),
    targetType: search.targetType ?? 'all',
    targetId: search.targetId ?? '',
  }));
  const debouncedQ = useDebounce(filters.q, 300);
  const debouncedActorId = useDebounce(filters.actorId, 300);

  /* Janela do período ancorada na seleção (não em cada render) — mantém a queryKey estável. */
  const range = useMemo(() => periodToRange(filters.period), [filters.period]);
  const serverQuery = useMemo<AuditQuery>(
    () => ({
      q: debouncedQ.length > 0 ? debouncedQ : undefined,
      actorId: showActorFilter && debouncedActorId.length > 0 ? debouncedActorId : undefined,
      targetType: filters.targetType !== 'all' ? filters.targetType : undefined,
      targetId: filters.targetId.length > 0 ? filters.targetId : undefined,
      action: filters.action !== 'all' ? filters.action : undefined,
      ...range,
    }),
    [
      debouncedQ,
      debouncedActorId,
      showActorFilter,
      filters.targetType,
      filters.targetId,
      filters.action,
      range,
    ],
  );

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAuditLog(serverQuery);
  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const hasFilters =
    debouncedQ.length > 0 ||
    (showActorFilter && debouncedActorId.length > 0) ||
    filters.period !== 'all' ||
    filters.targetType !== 'all' ||
    filters.targetId.length > 0 ||
    filters.action !== 'all';

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.title}</PageTitle>
          <PageDescription>{copy.description}</PageDescription>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-4">
        <AuditFilters value={filters} onChange={setFilters} showActorFilter={showActorFilter} />
        {filters.targetId.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">{copy.filteringByTarget}</span>
            <span className="inline-flex items-center gap-2 rounded-md border border-border bg-muted px-2.5 py-1">
              {filters.targetType !== 'all' && (
                <span className="text-muted-foreground">
                  {auditTargetTypeLabel(filters.targetType)}
                </span>
              )}
              <span className="font-mono text-foreground">{filters.targetId}</span>
              <button
                type="button"
                aria-label={copy.removeTargetFilter}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setFilters((prev) => ({ ...prev, targetId: '' }))}
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </span>
          </div>
        )}
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadFailed}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={5} />
        ) : (
          <>
            {items.length > 0 && (
              <div className="flex justify-end">
                <span className="text-sm text-muted-foreground tabular-nums">
                  {copy.loadedCount(items.length)}
                </span>
              </div>
            )}
            <AuditTable
              items={items}
              hasFilters={hasFilters}
              onClearFilters={() => setFilters(defaultAuditFilters())}
            />
          </>
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMore}
            </Button>
          </div>
        )}
      </PageContent>
    </Page>
  );
}
